import { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { KanbanCard } from './KanbanCard';
import { useOpportunityPipeline, useUpdateOpportunityStatusKanban, useMakeDecision } from '../../hooks/useOpportunities';
import type { Opportunity, OpportunityStatus, PipelineColumn } from '../../types/internal-opportunity.types';
import { STATUS_CONFIG } from '../../types/internal-opportunity.types';

interface KanbanColumnProps {
  column: PipelineColumn;
  children: React.ReactNode;
  isOver?: boolean;
}

function KanbanColumn({ column, children, isOver }: KanbanColumnProps) {
  const statusConfig = STATUS_CONFIG[column.id];
  const { setNodeRef } = useDroppable({
    id: column.id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[280px] max-w-[320px] rounded-lg transition-colors ${
        isOver ? 'bg-copper/10 ring-2 ring-copper/50' : 'bg-muted/30'
      }`}
    >
      {/* Header */}
      <div className="p-3 border-b bg-muted/50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${statusConfig.bgColor.replace('-100', '-500')}`} />
            <h3 className="font-medium text-sm">{column.title}</h3>
          </div>
          <span className="text-xs text-muted-foreground bg-background px-2 py-0.5 rounded-full">
            {column.count}
          </span>
        </div>
      </div>

      {/* Cards container */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-300px)] min-h-[200px]">
        {children}
      </div>
    </div>
  );
}

export function KanbanBoard() {
  const { data: pipeline, isLoading, error } = useOpportunityPipeline();
  const updateStatus = useUpdateOpportunityStatusKanban();
  const makeDecision = useMakeDecision();
  const [activeId, setActiveId] = useState<number | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const allOpportunities = useMemo(() => {
    if (!pipeline?.columns) return new Map<number, Opportunity>();
    const map = new Map<number, Opportunity>();
    pipeline.columns.forEach((col) => {
      col.opportunities.forEach((opp) => {
        map.set(opp.id, opp);
      });
    });
    return map;
  }, [pipeline]);

  // Map pour trouver le statut d'une opportunité par son ID
  const opportunityToStatus = useMemo(() => {
    if (!pipeline?.columns) return new Map<number, OpportunityStatus>();
    const map = new Map<number, OpportunityStatus>();
    pipeline.columns.forEach((col) => {
      col.opportunities.forEach((opp) => {
        map.set(opp.id, col.id);
      });
    });
    return map;
  }, [pipeline]);

  const activeOpportunity = activeId ? allOpportunities.get(activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as number;
    setActiveId(id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) {
      setOverColumnId(null);
      return;
    }

    // Déterminer sur quelle colonne on survole
    const overId = over.id;
    const columnIds = pipeline?.columns.map(c => c.id) || [];

    if (typeof overId === 'string' && columnIds.includes(overId as OpportunityStatus)) {
      setOverColumnId(overId);
    } else if (typeof overId === 'number') {
      // Survolant une carte - trouver sa colonne
      const cardStatus = opportunityToStatus.get(overId);
      setOverColumnId(cardStatus || null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setOverColumnId(null);

    if (!over) return;

    const opportunityId = active.id as number;
    const overId = over.id;

    // Déterminer la colonne cible
    let targetStatus: OpportunityStatus | null = null;
    const columnIds = pipeline?.columns.map(c => c.id) || [];

    if (typeof overId === 'string' && columnIds.includes(overId as OpportunityStatus)) {
      targetStatus = overId as OpportunityStatus;
    } else if (typeof overId === 'number') {
      targetStatus = opportunityToStatus.get(overId) || null;
    }

    if (!targetStatus) return;

    const currentStatus = opportunityToStatus.get(opportunityId);
    if (!currentStatus || currentStatus === targetStatus) return;

    // Validation : GO requis pour in_progress ou submitted
    const opportunity = allOpportunities.get(opportunityId);
    if (['in_progress', 'submitted'].includes(targetStatus) && opportunity?.decision !== 'go') {
      toast.error('Une décision GO est requise avant de passer en cours ou soumis');
      return;
    }

    updateStatus.mutate(
      { id: opportunityId, status: targetStatus },
      {
        onSuccess: () => toast.success('Statut mis à jour'),
        onError: (error: unknown) => {
          const axiosError = error as { response?: { data?: { message?: string } } };
          const msg = axiosError?.response?.data?.message || 'Erreur lors de la mise à jour du statut';
          toast.error(msg);
        },
      }
    );
  };

  const handleDecision = (id: number, decision: 'go' | 'no_go') => {
    makeDecision.mutate(
      { id, decision },
      {
        onSuccess: () => {
          toast.success(
            `Décision ${decision === 'go' ? 'GO' : 'NO-GO'} enregistrée`
          );
        },
        onError: () => {
          toast.error('Erreur lors de l\'enregistrement de la décision');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-copper" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        Erreur lors du chargement du pipeline
      </div>
    );
  }

  if (!pipeline?.columns) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Aucune donnée disponible
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {pipeline.columns.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            isOver={overColumnId === column.id}
          >
            {column.opportunities.length === 0 ? (
              <div className="flex items-center justify-center h-24 text-xs text-muted-foreground border-2 border-dashed rounded-lg">
                Déposez ici
              </div>
            ) : (
              column.opportunities.map((opportunity) => (
                <KanbanCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  isDragging={activeId === opportunity.id}
                  onDecision={handleDecision}
                />
              ))
            )}
          </KanbanColumn>
        ))}
      </div>

      <DragOverlay>
        {activeOpportunity && (
          <div className="rotate-3 scale-105">
            <KanbanCard opportunity={activeOpportunity} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
